import { useState, useEffect, type FormEvent } from 'react';
import { api } from '../../lib/api';
import { showToast } from '../../components/Toast';
import type { Student, Bus } from '../../lib/types';

export function StudentManager() {
  const [students, setStudents] = useState<Student[]>([]);
  const [buses, setBuses] = useState<Bus[]>([]);
  const [parents, setParents] = useState<{ id: string; name: string; email: string }[]>([]);
  const [stops, setStops] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', grade: '', parent_id: '', bus_id: '', stop_id: '' });
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      const [studentsRes, busesRes, parentsRes, stopsRes] = await Promise.all([
        api.getStudents(),
        api.getBuses(),
        api.getUsers('parent'),
        api.getStops(),
      ]);
      setStudents(studentsRes.students);
      setBuses(busesRes.buses);
      setParents(parentsRes.users || []);
      setStops(stopsRes.stops || []);
    } catch { showToast('Failed to load students'); } finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const resetForm = () => {
    setForm({ name: '', grade: '', parent_id: '', bus_id: '', stop_id: '' });
    setEditingId(null);
    setShowForm(false);
    setError(null);
  };

  const startEdit = (s: Student) => {
    setForm({
      name: s.name,
      grade: s.grade || '',
      parent_id: (s as any).parent_id || '',
      bus_id: (s as any).bus_id || '',
      stop_id: (s as any).stop_id || '',
    });
    setEditingId(s.id);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.parent_id) { setError('Name and Parent are required'); return; }
    try {
      if (editingId) {
        await api.updateStudent(editingId, {
          name: form.name,
          grade: form.grade || null,
          parent_id: form.parent_id,
          bus_id: form.bus_id || null,
          stop_id: form.stop_id || null,
        });
      } else {
        await api.createStudent({
          name: form.name,
          grade: form.grade || null,
          parent_id: form.parent_id,
          bus_id: form.bus_id || null,
          stop_id: form.stop_id || null,
        });
      }
      resetForm();
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Delete student "${name}"? This cannot be undone.`)) return;
    try {
      await api.deleteStudent(id);
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div>
      <div className="admin-section-header">
        <h2>Student Management</h2>
        <button className="btn-sm btn-primary" onClick={() => { if (showForm) resetForm(); else setShowForm(true); }}>
          {showForm ? 'Cancel' : '+ Add Student'}
        </button>
      </div>

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit} aria-label={editingId ? 'Edit student form' : 'Add student form'}>
          <div className="admin-form-row">
            <div className="form-group">
              <label>Student Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" required aria-label="Student name" />
            </div>
            <div className="form-group">
              <label>Grade</label>
              <input value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} placeholder="e.g., 5th" aria-label="Grade" />
            </div>
            <div className="form-group">
              <label>Parent</label>
              <select value={form.parent_id} onChange={(e) => setForm({ ...form, parent_id: e.target.value })} required aria-label="Select parent">
                <option value="">-- Select Parent --</option>
                {parents.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.email})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Bus</label>
              <select value={form.bus_id} onChange={(e) => setForm({ ...form, bus_id: e.target.value })} aria-label="Select bus">
                <option value="">-- Select Bus --</option>
                {buses.map((b) => (
                  <option key={b.id} value={b.id}>{b.bus_number}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Drop Stop</label>
              <select value={form.stop_id} onChange={(e) => setForm({ ...form, stop_id: e.target.value })} aria-label="Select drop stop">
                <option value="">-- Select Stop --</option>
                {stops.map((s: any) => (
                  <option key={s.id} value={s.id}>{s.name} ({s.route_name})</option>
                ))}
              </select>
            </div>
          </div>
          {error && <div className="form-error">{error}</div>}
          <button type="submit" className="btn-sm btn-primary">
            {editingId ? 'Update Student' : 'Add Student'}
          </button>
          {editingId && (
            <button type="button" className="btn-sm btn-outline" style={{ marginLeft: 8 }} onClick={resetForm}>
              Cancel Edit
            </button>
          )}
        </form>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Photo</th>
            <th>Name</th>
            <th>Grade</th>
            <th>Bus</th>
            <th>Stop</th>
            <th>Parent</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {students.map((s) => (
            <tr key={s.id}>
              <td>
                {s.photo_url
                  ? <img src={`/api/students/${s.id}/photo`} alt={s.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ display: 'inline-block', width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', textAlign: 'center', lineHeight: '36px', fontSize: 16 }}>👤</span>
                }
              </td>
              <td><strong>{s.name}</strong></td>
              <td>{s.grade || '--'}</td>
              <td>{s.bus_number || 'Unassigned'}</td>
              <td>{s.stop_name || 'Unassigned'}</td>
              <td>{(s as any).parent_name || '--'}</td>
              <td style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                <button className="btn-sm btn-outline" onClick={() => startEdit(s)} aria-label={`Edit student ${s.name}`}>Edit</button>
                <label className="btn-sm btn-outline" style={{ cursor: 'pointer', margin: 0 }} aria-label={`Upload photo for ${s.name}`}>
                  📷
                  <input type="file" accept="image/*" style={{ display: 'none' }} aria-label={`Upload photo for ${s.name}`} onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      await api.uploadStudentPhoto(s.id, file);
                      loadData();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Photo upload failed');
                    }
                    e.target.value = '';
                  }} />
                </label>
                <button className="btn-sm btn-danger" onClick={() => handleDelete(s.id, s.name)} aria-label={`Delete student ${s.name}`}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {students.length === 0 && <div className="admin-empty">No students enrolled yet.</div>}
    </div>
  );
}
